import { difference, filter, find, join, map } from 'lodash-es'
import type { GqlClient } from '@/types/gqlClient'
import { graphql, type VariablesOf } from 'gql.tada'
import { ProjectPlot } from 'types'

interface PlotComponent {
  prefix: string | undefined
  section: string | undefined
  number: string | undefined
  area: number | undefined
}

export type PlotLocality = {
  locality: string
  department_code: string | null
}

/**
 * A cadastral reference (prefix + section + number) is only unique inside one
 * commune: `000 AB 506` names 616 parcels in France (#746). The commune decides
 * which PLU a study is read against, so when it cannot be told apart this
 * refuses instead of taking whichever row came back last.
 */
export class PlotLocalityError extends Error {
  constructor(
    readonly plotLabel: string,
    readonly reason: 'NOT_FOUND' | 'AMBIGUOUS'
  ) {
    super(
      reason === 'AMBIGUOUS'
        ? `La parcelle ${plotLabel} existe dans plusieurs communes et le code postal ne permet pas de savoir laquelle. Vérifiez le code postal, puis resélectionnez la parcelle sur la carte.`
        : `La parcelle ${plotLabel} est introuvable dans le cadastre. Resélectionnez-la sur la carte, ou saisissez-la manuellement avec sa commune.`
    )
    this.name = 'PlotLocalityError'
  }
}

/**
 * The department(s) a postal code belongs to. A postal code is not a commune,
 * but it narrows a cadastral reference from the whole of France to one
 * department. Corsican codes (20xxx) span both 2A and 2B.
 */
export const departmentsForPostalCode = (
  postalCode: string | null | undefined
): Array<string> => {
  const value = postalCode?.trim() ?? ''
  if (!/^\d{5}$/.test(value)) return []
  if (value.startsWith('20')) return ['2A', '2B']
  if (/^9[78]/.test(value)) return [value.slice(0, 3)]
  return [value.slice(0, 2)]
}

/** Mirrors the SQL `extract_department_code_from_locality`. */
export const departmentFromLocality = (locality: string): string | null => {
  const value = locality.trim().toUpperCase()
  if (/^9[78]\d{3}$/.test(value)) return value.slice(0, 3)
  if (/^(2A|2B|\d{2})\d{3}$/.test(value)) return value.slice(0, 2)
  return null
}

const PLOT_LOCALITIES_QUERY = graphql(`
  query GetPlotLocalities($where: plots_bool_exp!) {
    plots(
      where: $where
      distinct_on: [locality]
      order_by: [{ locality: asc }]
      limit: 2
    ) {
      locality
      department_code
    }
  }
`)

type PlotWhere = VariablesOf<typeof PLOT_LOCALITIES_QUERY>['where']

const plotLabel = (plot: PlotComponent) =>
  [plot.prefix, plot.section, plot.number].filter(Boolean).join(' ')

/**
 * Resolve the commune of a parcel from the national cadastre.
 *
 * Two distinct localities are enough to know the answer is not unique, hence
 * `limit: 2`. The postal code is tried first; the whole of France only when
 * no parcel with that reference sits in the postal code's department (a postal
 * code can straddle a department border), and then only a unique match counts.
 */
export const resolvePlotLocality = async (
  gqlClient: GqlClient,
  plot: PlotComponent,
  postalCode?: string | null
): Promise<PlotLocality> => {
  const label = plotLabel(plot)
  if (!plot.section || !plot.number) {
    throw new PlotLocalityError(label, 'NOT_FOUND')
  }
  const reference: Array<PlotWhere> = [
    ...(plot.prefix ? [{ prefix: { _eq: plot.prefix } }] : []),
    { section: { _eq: plot.section } },
    { number: { _eq: plot.number } },
    ...(plot.area != null ? [{ area: { _eq: plot.area } }] : []),
  ]
  const findLocalities = async (
    where: Array<PlotWhere>
  ): Promise<Array<PlotLocality>> => {
    const result = await gqlClient.request(PLOT_LOCALITIES_QUERY, {
      where: { _and: where },
    })
    return result.plots
  }

  const departments = departmentsForPostalCode(postalCode)
  if (departments.length > 0) {
    const local = await findLocalities([
      ...reference,
      { department_code: { _in: departments } },
    ])
    if (local.length === 1) return local[0]
    if (local.length > 1) throw new PlotLocalityError(label, 'AMBIGUOUS')
  }

  const national = await findLocalities(reference)
  if (national.length === 1) return national[0]
  throw new PlotLocalityError(
    label,
    national.length === 0 ? 'NOT_FOUND' : 'AMBIGUOUS'
  )
}

/**
 * Give every plot its commune. A plot picked on the map already carries it
 * (the tile is cut from the same `plots` row), as does a manually entered one;
 * only a plot missing it goes back to the cadastre, and that lookup refuses
 * rather than guesses.
 */
export const withPlotLocalities = <T extends ProjectPlot>(
  gqlClient: GqlClient,
  plots: Array<T>,
  postalCode?: string | null
): Promise<Array<T & PlotLocality>> =>
  Promise.all(
    map(plots, async (plot) => {
      if (plot.locality) {
        return {
          ...plot,
          locality: plot.locality,
          department_code:
            plot.department_code || departmentFromLocality(plot.locality),
        }
      }
      return {
        ...plot,
        ...(await resolvePlotLocality(gqlClient, plot, postalCode)),
      }
    })
  )

export const removeProjectPlots = (
  gqlClient: GqlClient,
  projectID: number,
  plotSlugsToRemove: Array<string>
) => {
  const formatedPlots = join(
    map(
      plotSlugsToRemove,
      (plotID) =>
        `{ _and: { project_id: {_eq: $project_id}, slug: {_eq: "${plotID}"} } }`
    ),
    ','
  )
  const query = graphql(`
  mutation ($project_id: Int) {
    delete_project_plots(where: {
      _or: [
        ${formatedPlots}
      ]
    }) {
      affected_rows
    }
  }
  `)
  return gqlClient.request(query, { project_id: projectID })
}

export const addProjectPlots = async (
  gqlClient: GqlClient,
  projectID: number,
  projectPlotToAdd: Array<ProjectPlot>,
  mainPlotsSlug: string,
  postalCode?: string | null
) => {
  const plots = await withPlotLocalities(gqlClient, projectPlotToAdd, postalCode)

  // Note: code is now a generated column, so it's not included in the insert
  const formatedPlots = join(
    map(plots, (plot) => {
      const locality = plot.locality
      const departmentCode = plot.department_code

      const escapedLocality = locality.replace(/"/g, '\\"')
      const escapedDepartmentCode = departmentCode
        ? departmentCode.replace(/"/g, '\\"')
        : null

      return `{
          project_id: $project_id,
          main_plot: ${mainPlotsSlug == plot.slug},
          prefix: "${plot.prefix}",
          section: "${plot.section}",
          number: "${plot.number}",
          area: ${plot.area},
          locality: "${escapedLocality}",
          department_code: ${escapedDepartmentCode ? `"${escapedDepartmentCode}"` : 'null'}
        }`
    }),
    ','
  )

  const query = graphql(`
  mutation ($project_id: Int) {
    insert_project_plots(objects: [
      ${formatedPlots}
    ]) {
      returning {
        slug
      }
    }
  }
  `)
  return gqlClient.request(query, { project_id: projectID })
}

export const updateProjectMainPlot = (
  gqlClient: GqlClient,
  projectID: number,
  prevProjectMainPlot: string | undefined,
  newProjectMainPlot: string
) => {
  const disablePrevMainPlotQuery = prevProjectMainPlot
    ? `
    {
      where: {
        project_id: {_eq: $project_id},
        slug: {_eq: "${prevProjectMainPlot}" }
      },
      _set: { main_plot: false }
    },
  `
    : ''
  const query = graphql(`
  mutation ($project_id: Int) {
    update_project_plots_many(
      updates: [
        ${disablePrevMainPlotQuery}
        {
          where: {
            project_id: {_eq: $project_id},
            slug: {_eq: "${newProjectMainPlot}" }
          },
          _set: { main_plot: true }
        }
      ]
    ) {
      affected_rows
    }
  }
  `)
  return gqlClient.request(query, { project_id: projectID })
}

export const updateSelectedPlots = (
  gqlClient: GqlClient,
  projectID: number,
  projectPlots: Array<ProjectPlot>,
  selectedPlots: Array<ProjectPlot> | undefined,
  mainPlotSlug: string | undefined,
  onFinish: () => void,
  options: {
    /** Narrows the cadastre lookup of a plot that carries no commune. */
    postalCode?: string | null
    /** A plot that cannot be placed in one commune is refused, not guessed. */
    onError?: (error: unknown) => void
  } = {}
) => {
  if (projectID) {
    const selectedPlotsSlugs = map(selectedPlots, 'slug') as [string]
    const existingPlotsSlugs = map(projectPlots, 'slug') as [string]
    const addedPlotSlugs = difference(selectedPlotsSlugs, existingPlotsSlugs)
    const addedPlots = filter(selectedPlots, (plot) =>
      addedPlotSlugs.includes(plot.slug)
    )
    const removedPlotSlugs = difference(existingPlotsSlugs, selectedPlotsSlugs)
    const prevMainPlotSlug = find(projectPlots, 'main_plot')?.slug

    if (addedPlotSlugs.length && mainPlotSlug) {
      addProjectPlots(
        gqlClient,
        projectID,
        addedPlots,
        mainPlotSlug,
        options.postalCode
      ).then(onFinish, options.onError ?? console.error)
    }
    if (removedPlotSlugs.length) {
      removeProjectPlots(gqlClient, projectID, removedPlotSlugs).then(onFinish)
    }
    if (mainPlotSlug && prevMainPlotSlug != mainPlotSlug) {
      updateProjectMainPlot(
        gqlClient,
        projectID,
        prevMainPlotSlug,
        mainPlotSlug
      ).then(onFinish)
    }
  }
}
